import { Column } from '@ant-design/plots';
import { Card, Col, DatePicker, Empty, message, Row, Select, Space, Spin, Typography } from 'antd';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import useDebounce from '~/hooks/useDebounce';
import api from '~/services/api';

import { Container } from './styles';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const RETURN_REASONS = [
  'Claim damage',
  'Delivery issue',
  'Different/Wrong Item',
  'Repair service',
  'No reason given',
  'Overstock',
  'Product dissatisfaction',
  'Defective',
  'Wrong description',
  'No longer needed',
];

const RETURN_REASON_OPTIONS = RETURN_REASONS.map((reason) => ({ value: reason, label: reason }));

const DEFAULT_FILTERS = {
  dateRange: [moment().subtract(1, 'year').startOf('month'), moment()],
  storeId: null,
  returnReason: null,
};

const DEBOUNCE_MS = 400;

export function RMAWithinADay() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [stores, setStores] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedFilters = useDebounce(filters, DEBOUNCE_MS);

  useEffect(() => {
    async function fetchStores() {
      try {
        const res = await api.get('stores');
        setStores(res.data.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name })));
      } catch {
        setStores([]);
      }
    }
    fetchStores();
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const params = {
          startDate: debouncedFilters.dateRange[0].format('YYYY-MM-DD'),
          endDate: debouncedFilters.dateRange[1].format('YYYY-MM-DD'),
        };
        if (debouncedFilters.storeId) params.storeId = debouncedFilters.storeId;
        if (debouncedFilters.returnReason) params.returnReason = debouncedFilters.returnReason;
        const res = await api.get('kpi/RMAWithinADay', { params });
        setData(res.data || []);
      } catch (err) {
        setError('Failed to load KPI data.');
        message.error('Failed to load KPI data.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [debouncedFilters]);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  // Summary calculations
  const totalRMAs = data.reduce((sum, d) => sum + (d.total || 0), 0);
  const lateRMAs = data.reduce((sum, d) => sum + (d.late || 0), 0);
  // const onTimeRMAs = totalRMAs - lateRMAs; // Not used, so removed
  const percentOnTime = totalRMAs > 0 ? (((totalRMAs - lateRMAs) / totalRMAs) * 100).toFixed(1) : '0.0';

  // Chart config
  const chartData = data.map((d) => ({
    month: `${d.month} ${d.year}`,
    'On-time %': d.percent,
    total: d.total,
    late: d.late,
  }));

  const chartConfig = {
    data: chartData,
    xField: 'month',
    yField: 'On-time %',
    columnWidthRatio: 0.7,
    color: '#639922',
    yAxis: {
      min: 0,
      max: 100,
      label: { formatter: (v) => `${v}%` },
      title: { text: 'On-time %' },
    },
    xAxis: { label: { autoRotate: true } },
    label: {
      position: 'top',
      style: { fontSize: 11, fill: '#555' },
      content: (item) => `${item['On-time %']}%`,
    },
    tooltip: {
      customContent: (title, items) => {
        const d = items?.[0]?.data || {};
        return `
          <div style="padding:8px 10px;min-width:180px">
            <div style="font-weight:600;margin-bottom:6px">${d.month || ''}</div>
            <div>Total RMAs: <b>${d.total ?? '-'}</b></div>
            <div>Late RMAs: <b>${d.late ?? '-'}</b></div>
            <div>On-time %: <b>${d['On-time %'] ?? '-'}</b></div>
          </div>
        `;
      },
    },
    height: 340,
  };

  return (
    <Container>
      <Title level={3}>RMA Processed Within a Day KPI</Title>

      <Card title="Filters" size="small" style={{ marginBottom: 18 }}>
        <Row gutter={[16, 14]}>
          <Col xs={24} md={12} xl={8}>
            <Text strong>Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 4 }}
              value={filters.dateRange}
              format="YYYY-MM-DD"
              allowClear={false}
              onChange={(dates) => dates && updateFilter('dateRange', dates)}
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Text strong>Store</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All stores"
              options={stores}
              value={filters.storeId}
              onChange={(val) => updateFilter('storeId', val)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Text strong>Return Reason</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All reasons"
              options={RETURN_REASON_OPTIONS}
              value={filters.returnReason}
              onChange={(val) => updateFilter('returnReason', val)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading} tip="Loading report...">
        {error ? (
          <Empty description={error} style={{ margin: '60px 0' }} />
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
              <Col xs={24} md={8}>
                <Card>
                  <Space direction="vertical" size={2}>
                    <Text strong>Total RMAs</Text>
                    <Title level={4} style={{ margin: 0 }}>
                      {totalRMAs}
                    </Title>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Space direction="vertical" size={2}>
                    <Text strong>Late RMAs</Text>
                    <Title level={4} style={{ margin: 0, color: '#E24B4A' }}>
                      {lateRMAs}
                    </Title>
                  </Space>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Space direction="vertical" size={2}>
                    <Text strong>On-time %</Text>
                    <Title level={4} style={{ margin: 0, color: '#639922' }}>
                      {percentOnTime}%
                    </Title>
                  </Space>
                </Card>
              </Col>
            </Row>
            <Card title="Monthly On-time KPI" bodyStyle={{ padding: 0 }}>
              {chartData.length ? (
                <div style={{ padding: 18 }}>
                  <Column {...chartConfig} />
                </div>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data for selected filters" style={{ margin: '60px 0' }} />
              )}
            </Card>
          </>
        )}
      </Spin>
    </Container>
  );
}
